/**
 * authService — wraps Cognito OAuth flow (Hosted UI).
 *
 * Manages tokens in memory (not localStorage for security).
 * Uses refresh token for silent renewal.
 * Falls back to anonymous on any failure.
 */

import { setAuthToken, setIdToken } from './apiClient';

export interface AuthUser {
  sub: string;
  email: string;
  name: string;
  picture: string | null;
}

interface TokenSet {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // Unix ms
}

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN || '';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const REDIRECT_URI = `${window.location.origin}/auth/callback`;
const REGION = import.meta.env.VITE_AWS_REGION || 'eu-west-1';

/** Build the Cognito Hosted UI base URL */
function getCognitoBaseUrl(): string {
  // For local dev, just use the domain directly
  if (COGNITO_DOMAIN.includes('localhost') || COGNITO_DOMAIN.includes('://')) {
    return COGNITO_DOMAIN;
  }
  return `https://${COGNITO_DOMAIN}.auth.${REGION}.amazoncognito.com`;
}

/** Check if running in local dev mode */
function isLocalDev(): boolean {
  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocalHost && (COGNITO_DOMAIN.includes('localhost') || COGNITO_DOMAIN === '');
}

/** Get the base URL for dev auth endpoints (relative path uses Vite proxy) */
function getDevAuthUrl(): string {
  return '';
}

/** Session storage key for token persistence across page refreshes */
const TOKEN_STORAGE_KEY = 'lunch-finder-auth-tokens';

/** In-memory token storage */
let tokens: TokenSet | null = null;
let currentUser: AuthUser | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/** Persist tokens to sessionStorage */
function persistTokens(): void {
  if (tokens) {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  } else {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

/** Restore tokens from sessionStorage on init */
function restoreTokens(): void {
  try {
    const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!stored) return;

    const parsed: TokenSet = JSON.parse(stored);
    // Only restore if not expired
    if (parsed.expiresAt > Date.now()) {
      tokens = parsed;
      currentUser = extractUser(tokens.idToken);
      setAuthToken(tokens.accessToken);
    setIdToken(tokens.idToken);
      scheduleRefresh();
    } else {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

/** Listeners for auth state changes */
type AuthListener = (user: AuthUser | null) => void;
const listeners: Set<AuthListener> = new Set();

function notifyListeners(): void {
  listeners.forEach((fn) => fn(currentUser));
}

/**
 * Parse JWT payload without verification (browser-side).
 * Verification happens server-side via API Gateway/Cognito authorizer.
 */
function parseJwtPayload(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1];
  if (!base64Url) return {};
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(json);
}

function extractUser(idToken: string): AuthUser {
  const payload = parseJwtPayload(idToken);
  return {
    sub: (payload.sub as string) || '',
    email: (payload.email as string) || '',
    name: (payload.name as string) || (payload.email as string) || '',
    picture: (payload.picture as string) || null,
  };
}

function scheduleRefresh(): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  if (!tokens) return;

  // Refresh 5 minutes before expiry
  const refreshIn = tokens.expiresAt - Date.now() - 5 * 60 * 1000;
  if (refreshIn <= 0) {
    // Already expired or about to — refresh now
    void refreshTokens();
    return;
  }

  refreshTimer = setTimeout(() => {
    void refreshTokens();
  }, refreshIn);
}

async function refreshTokens(): Promise<boolean> {
  if (!tokens?.refreshToken) return false;

  try {
    let tokenUrl: string;

    if (isLocalDev()) {
      tokenUrl = `${getDevAuthUrl()}/auth/dev-token-exchange`;
    } else {
      const baseUrl = getCognitoBaseUrl();
      tokenUrl = `${baseUrl}/oauth2/token`;
    }

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: CLIENT_ID,
        refresh_token: tokens.refreshToken,
      }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed: ${response.status}`);
    }

    const data = await response.json();
    tokens = {
      idToken: data.id_token,
      accessToken: data.access_token,
      refreshToken: tokens.refreshToken, // refresh token is not rotated
      expiresAt: Date.now() + data.expires_in * 1000,
    };

    currentUser = extractUser(tokens.idToken);
    setAuthToken(tokens.accessToken);
    setIdToken(tokens.idToken);
    persistTokens();
    scheduleRefresh();
    notifyListeners();
    return true;
  } catch {
    // Refresh failed — revert to anonymous
    logout();
    return false;
  }
}

/**
 * Exchange authorization code for tokens (called from callback page).
 */
async function handleCallback(code: string): Promise<boolean> {
  try {
    let tokenUrl: string;
    let body: URLSearchParams;

    if (isLocalDev()) {
      // Use local dev-server token exchange endpoint
      tokenUrl = `${getDevAuthUrl()}/auth/dev-token-exchange`;
      body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: CLIENT_ID,
        code,
        redirect_uri: REDIRECT_URI,
      });
    } else {
      const baseUrl = getCognitoBaseUrl();
      tokenUrl = `${baseUrl}/oauth2/token`;
      body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: CLIENT_ID,
        code,
        redirect_uri: REDIRECT_URI,
      });
    }

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    const data = await response.json();
    tokens = {
      idToken: data.id_token,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresAt: Date.now() + data.expires_in * 1000,
    };

    currentUser = extractUser(tokens.idToken);
    setAuthToken(tokens.accessToken);
    setIdToken(tokens.idToken);
    persistTokens();
    scheduleRefresh();
    notifyListeners();
    return true;
  } catch {
    logout();
    return false;
  }
}

/**
 * Initiate login.
 * - In local dev: calls dev-login endpoint directly (no Cognito Hosted UI needed)
 * - In production: redirects to Cognito Hosted UI with Google
 */
function login(): void {
  if (isLocalDev()) {
    // In dev mode, call the backend dev-login endpoint directly
    devLogin();
    return;
  }

  const baseUrl = getCognitoBaseUrl();
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: 'openid email profile',
    identity_provider: 'Google',
  });
  window.location.href = `${baseUrl}/oauth2/authorize?${params.toString()}`;
}

/**
 * Dev-only login — calls /auth/dev-login on the backend and sets tokens directly.
 */
async function devLogin(): Promise<void> {
  try {
    const response = await fetch(`${getDevAuthUrl()}/auth/dev-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: 'dev@localhost', name: 'Dev User' }),
    });

    if (!response.ok) {
      throw new Error(`Dev login failed: ${response.status}`);
    }

    const data = await response.json();
    tokens = {
      idToken: data.id_token,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresAt: Date.now() + data.expires_in * 1000,
    };

    currentUser = extractUser(tokens.idToken);
    setAuthToken(tokens.accessToken);
    setIdToken(tokens.idToken);
    persistTokens();
    scheduleRefresh();
    notifyListeners();
  } catch (err) {
    console.error('Dev login failed:', err);
    logout();
  }
}

/**
 * Logout — invalidate session, clear tokens, revert to anonymous.
 */
function logout(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  tokens = null;
  currentUser = null;
  setAuthToken(null);
  setIdToken(null);
  persistTokens();
  notifyListeners();
}

/**
 * Full logout with Cognito session invalidation (redirect to logout endpoint).
 */
function logoutWithRedirect(): void {
  const baseUrl = getCognitoBaseUrl();
  logout();
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: window.location.origin,
  });
  window.location.href = `${baseUrl}/logout?${params.toString()}`;
}

function getAccessToken(): string | null {
  return tokens?.accessToken ?? null;
}

function getUser(): AuthUser | null {
  return currentUser;
}

function isLoggedIn(): boolean {
  return currentUser !== null && tokens !== null && tokens.expiresAt > Date.now();
}

function subscribe(listener: AuthListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Restore session on module load
restoreTokens();

export const authService = {
  login,
  logout: logoutWithRedirect,
  handleCallback,
  getAccessToken,
  getUser,
  isLoggedIn,
  subscribe,
  /** Internal logout without redirect (used for failed refresh) */
  _clearSession: logout,
};
